import { useState, useEffect, useMemo } from 'react'
import { loadQuotesWithData } from '../lib/quotes'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts'

const RANGES = [
  { label: 'All Time',      months: null },
  { label: 'This Month',    months: 1    },
  { label: 'Last 3 Months', months: 3    },
  { label: 'Last 6 Months', months: 6    },
]

const CONVERTED_STATUSES = new Set(['Confirmed', 'In Production', 'Ready', 'Delivered'])
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

const STATUS_LIST = ['Quoted', 'Confirmed', 'In Production', 'Ready', 'Delivered']
const STATUS_COLORS = {
  'Quoted':        '#aaa',
  'Confirmed':     '#4a9eff',
  'In Production': '#f0a500',
  'Ready':         '#7bc67e',
  'Delivered':     '#9b2d7f',
}

function fmt(n) {
  if (n == null || isNaN(n)) return '₹0'
  return '₹' + Math.round(n).toLocaleString('en-IN')
}

function startOfRange(months) {
  if (!months) return null
  const d = new Date()
  d.setMonth(d.getMonth() - months + 1)
  d.setDate(1)
  d.setHours(0, 0, 0, 0)
  return d
}

function last6MonthKeys() {
  const keys = []
  const d = new Date()
  for (let i = 5; i >= 0; i--) {
    const t = new Date(d.getFullYear(), d.getMonth() - i, 1)
    const key = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2,'0')}`
    keys.push({ key, label: MONTH_SHORT[t.getMonth()] })
  }
  return keys
}

function buildMonthlyData(quotes) {
  const buckets = last6MonthKeys()
  const countMap = {}, valueMap = {}
  buckets.forEach(({ key }) => { countMap[key] = 0; valueMap[key] = 0 })

  quotes.forEach(q => {
    if (!q.quote_date) return
    const d = new Date(q.quote_date)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}`
    if (key in countMap) {
      countMap[key] += 1
      valueMap[key] += q.grand_total || 0
    }
  })

  return buckets.map(({ key, label }) => ({
    month: label,
    count: countMap[key],
    value: Math.round(valueMap[key]),
  }))
}

function daysAgo(dateStr) {
  if (!dateStr) return null
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000)
}

function extractRoomData(quotes) {
  const panels = []
  const roomEntries = []
  let validQuoteCount = 0
  for (const q of quotes) {
    try {
      const qd = q.quote_data
      if (!qd || !Array.isArray(qd.rooms)) continue
      validQuoteCount++
      for (const room of qd.rooms) {
        roomEntries.push({ room, quote: q })
        if (!Array.isArray(room.windows)) continue
        for (const win of room.windows) {
          if (!Array.isArray(win.panels)) continue
          for (const panel of win.panels) {
            panels.push({ panel, room, win, quote: q })
          }
        }
      }
    } catch (e) {
      // skip malformed quote_data
    }
  }
return { panels, roomEntries, validQuoteCount }
}

const cardStyle = {
  background: '#fff',
  border: '1px solid #eee',
  borderRadius: 10,
  padding: 24,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
}

const kpiCardStyle = {
  ...cardStyle,
  borderTop: '3px solid #9b2d7f',
}

const chartTitleStyle = {
  fontSize: 15,
  fontWeight: 600,
  color: '#333',
  marginBottom: 12,
}

const interpStyle = {
  fontSize: 12,
  color: '#888',
  fontStyle: 'italic',
  marginTop: 6,
}

export default function AnalyticsDashboard({ onBack }) {
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rangeIdx, setRangeIdx] = useState(0)
  const [hoveredStatus, setHoveredStatus] = useState(null)

  useEffect(() => {
    loadQuotesWithData()
      .then(setQuotes)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    const cutoff = startOfRange(RANGES[rangeIdx].months)
    if (!cutoff) return quotes
    return quotes.filter(q => q.quote_date && new Date(q.quote_date) >= cutoff)
  }, [quotes, rangeIdx])

  const totalQuotes = filtered.length
  const totalValue = filtered.reduce((s, q) => s + (q.grand_total || 0), 0)
  const converted = filtered.filter(q => CONVERTED_STATUSES.has(q.status)).length
  const conversionRate = totalQuotes > 0 ? Math.round((converted / totalQuotes) * 100) : 0
  const avgValue = totalQuotes > 0 ? totalValue / totalQuotes : 0

  const kpis = [
    { label: 'Total Quotes',    value: totalQuotes },
    { label: 'Total Value',     value: fmt(totalValue) },
    { label: 'Conversion Rate', value: `${conversionRate}%` },
    { label: 'Avg Quote Value', value: fmt(avgValue) },
  ]

  const monthlyData = useMemo(() => buildMonthlyData(quotes), [quotes])

  const bestMonth = useMemo(() => {
    const monthMap = {}
    filtered.forEach(q => {
      if (!q.quote_date) return
      const d = new Date(q.quote_date)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      if (!monthMap[key]) monthMap[key] = { value: 0, count: 0, month: d.getMonth(), year: d.getFullYear() }
      monthMap[key].value += q.grand_total || 0
      monthMap[key].count++
    })
    const entries = Object.values(monthMap).filter(m => m.value > 0)
    if (entries.length === 0) return null
    const best = entries.reduce((a, b) => b.value > a.value ? b : a)
    return { label: `${MONTH_SHORT[best.month]} ${best.year}`, value: best.value, count: best.count }
  }, [filtered])

  function exportCSV() {
    const headers = ['Quote No', 'Customer', 'Address', 'Date', 'Grand Total', 'To Be Paid', 'Advance Paid', 'Status']
    const rows = filtered.map(q => [
      q.display_quote_no || '',
      q.customer_name || '',
      q.address || '',
      q.quote_date || '',
      Math.round(q.grand_total || 0),
      Math.round(q.to_be_paid || 0),
      q.advance_paid ? 'Yes' : 'No',
      q.status || '',
    ])
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `drapeiq-quotes-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const insights = useMemo(() => {
    const total = filtered.length

    // Status breakdown with quote numbers per status
    const statusCounts = {}
    const statusQuoteNos = {}
    STATUS_LIST.forEach(s => { statusCounts[s] = 0; statusQuoteNos[s] = [] })
    filtered.forEach(q => {
      if (q.status in statusCounts) {
        statusCounts[q.status]++
        statusQuoteNos[q.status].push(q.display_quote_no || String(q.id))
      }
    })
    const statusBreakdown = STATUS_LIST.map(s => ({
      status: s,
      count: statusCounts[s],
      pct: total > 0 ? Math.round((statusCounts[s] / total) * 100) : 0,
      quoteNos: statusQuoteNos[s],
    }))

    // Last quote date per customer (for top lists)
    const byLastDate = {}
    filtered.forEach(q => {
      const name = q.customer_name || 'Unknown'
      const d = q.quote_date || ''
      if (!byLastDate[name] || d > byLastDate[name]) byLastDate[name] = d
    })

    // Top 5 by value
    const byValue = {}
    filtered.forEach(q => {
      const name = q.customer_name || 'Unknown'
      byValue[name] = (byValue[name] || 0) + (q.grand_total || 0)
    })
    const top5ByValue = Object.entries(byValue)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value], i) => ({ rank: i + 1, name, value, lastDate: byLastDate[name] || null }))

    // Top 5 by count
    const byCount = {}
    filtered.forEach(q => {
      const name = q.customer_name || 'Unknown'
      byCount[name] = (byCount[name] || 0) + 1
    })
    const top5ByCount = Object.entries(byCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count], i) => ({ rank: i + 1, name, count, lastDate: byLastDate[name] || null }))

    // Repeat customers
    const repeatCustomers = Object.values(byCount).filter(c => c > 1).length

    // Revision rate
    const withRevisions = filtered.filter(q => q.revision > 0).length
    const revisionRate = total > 0 ? Math.round((withRevisions / total) * 100) : 0
    const avgRevisions = total > 0
      ? (filtered.reduce((s, q) => s + (q.revision || 0), 0) / total).toFixed(1)
      : '0.0'

    // Follow-up needed: status Quoted + older than 45 days
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - 45)
    const inactiveQuotes = filtered.filter(q =>
      q.status === 'Quoted' && q.quote_date && new Date(q.quote_date) < cutoffDate
    ).length

    // Amount to collect: to_be_paid for Confirmed/In Production where advance not yet paid
    const pipeline = filtered
      .filter(q => ['Confirmed', 'In Production', 'Ready', 'Delivered'].includes(q.status) && !q.advance_paid)
      .reduce((s, q) => s + (q.to_be_paid || 0), 0)

    // Realisation rate: paid advances / expected advances (all active statuses)
    const paidAdvances = filtered
      .filter(q => q.advance_paid)
      .reduce((s, q) => s + (q.to_be_paid || 0), 0)
    const expectedAdvances = filtered
      .filter(q => ['Confirmed', 'In Production', 'Ready', 'Delivered'].includes(q.status))
      .reduce((s, q) => s + (q.to_be_paid || 0), 0)
    const realisationRate = expectedAdvances > 0 ? Math.round((paidAdvances / expectedAdvances) * 100) : null

    return {
      statusBreakdown, top5ByValue, top5ByCount,
      repeatCustomers, revisionRate, avgRevisions,
      inactiveQuotes, pipeline, realisationRate,
    }
  }, [filtered])

  const productInsights = useMemo(() => {
    const { panels, roomEntries, validQuoteCount } = extractRoomData(filtered)

    // 1. Segment breakdown — inferred from curtainType
    const segCounts = { 'Curtain/Sheer': 0, 'Roman': 0, 'Readymade': 0 }
    panels.forEach(({ panel }) => {
      const ct = panel.curtainType || ''
      let seg
      if (ct.includes('Roman')) seg = 'Roman'
      else if (ct.includes('Readymade')) seg = 'Readymade'
      else seg = 'Curtain/Sheer'
      segCounts[seg]++
    })
    const totalPanels = panels.length
    const segments = Object.entries(segCounts).map(([type, count]) => ({
      type,
      count,
      pct: totalPanels > 0 ? Math.round((count / totalPanels) * 100) : 0,
    }))
    const maxSegCount = Math.max(...segments.map(s => s.count), 0)

    // 2. Top 5 room types
    const roomTypeCounts = {}
    roomEntries.forEach(({ room }) => {
      const rt = room.roomType || 'Unknown'
      roomTypeCounts[rt] = (roomTypeCounts[rt] || 0) + 1
    })
    const top5RoomTypes = Object.entries(roomTypeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([type, count], i) => ({ rank: i + 1, type, count }))

    // 3. Top 5 manufacturers (skip blank)
    const mfrCounts = {}
    panels.forEach(({ panel }) => {
      const mfr = (panel.mfr || '').trim()
      if (!mfr) return
      mfrCounts[mfr] = (mfrCounts[mfr] || 0) + 1
    })
    const top5Mfrs = Object.entries(mfrCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([mfr, count], i) => ({ rank: i + 1, mfr, count }))

    // 4. Average discount (panels with discount > 0)
    let discSum = 0, discCount = 0
    panels.forEach(({ panel }) => {
      const d = parseFloat(panel.discount)
      if (!isNaN(d) && d > 0) { discSum += d; discCount++ }
    })
    const avgDisc = discCount > 0 ? (discSum / discCount).toFixed(1) : null

    // 5. Average panels per quote
    const avgPanelsPerQuote = validQuoteCount > 0 ? (totalPanels / validQuoteCount).toFixed(1) : null

    // 6. Average rooms per quote
    const avgRoomsPerQuote = validQuoteCount > 0 ? (roomEntries.length / validQuoteCount).toFixed(1) : null

    // 7. Hardware attach rate — quote has hardware if any room has room.hardware items
    const quotesWithHardware = new Set()
    roomEntries.forEach(({ room, quote }) => {
      if (room.hardware?.length > 0) quotesWithHardware.add(quote.id)
    })
    const hardwareAttachRate = validQuoteCount > 0
      ? Math.round((quotesWithHardware.size / validQuoteCount) * 100)
      : null

    // 8. Fitting rate distribution
    const fittingRates = {}
    filtered.forEach(q => {
      try {
        const rate = String(q.quote_data?.fitting?.rate || '').trim()
        if (!rate) return
        fittingRates[rate] = (fittingRates[rate] || 0) + 1
      } catch (e) {}
    })
    const fittingDist = Object.entries(fittingRates)
      .sort((a, b) => b[1] - a[1])
      .map(([rate, count]) => ({ rate, count }))

    return {
      segments, maxSegCount, top5RoomTypes, top5Mfrs,
      avgDisc, avgPanelsPerQuote, avgRoomsPerQuote,
      hardwareAttachRate, fittingDist, validQuoteCount,
    }
  }, [filtered])

  return (
    <div style={{ minHeight: '100vh', background: '#f7f3f6', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid #eee',
        padding: '16px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            onClick={onBack}
            style={{
              background: 'none', border: 'none', color: '#a07090',
              fontSize: 12, cursor: 'pointer', letterSpacing: '0.5px',
              opacity: 0.75, fontFamily: 'inherit', padding: 0,
            }}
          >
            ← Home
          </button>
          <img src="/CS_Software_DinoLogo.png" alt="DrapeIQ" onClick={() => onBack()} style={{ height: 56, objectFit: 'contain', cursor: 'pointer' }} />
        </div>
        <span style={{ fontSize: 22, fontWeight: 700, color: '#9b2d7f' }}>Analytics</span>
        <div style={{ width: 160 }} />
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px' }}>
        {/* Date range filter + export */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          {RANGES.map((r, i) => (
            <button
              key={r.label}
              onClick={() => setRangeIdx(i)}
              style={{
                background: rangeIdx === i ? '#9b2d7f' : '#fff',
                color: rangeIdx === i ? '#fff' : '#9b2d7f',
                border: '1.5px solid #9b2d7f',
                borderRadius: 8,
                padding: '7px 18px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {r.label}
            </button>
          ))}
          <button
            onClick={exportCSV}
            style={{
              marginLeft: 'auto',
              background: '#fff',
              color: '#9b2d7f',
              border: '1px solid #9b2d7f',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Export CSV
          </button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', color: '#aaa', fontSize: 15, marginTop: 60 }}>Loading...</div>
        )}
        {error && (
          <div style={{ textAlign: 'center', color: '#b91c1c', fontSize: 14, marginTop: 60 }}>{error}</div>
        )}

        {!loading && !error && (
          <>
            {/* KPI cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 18,
              marginBottom: 28,
            }}>
              {kpis.map(k => (
                <div key={k.label} style={kpiCardStyle}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>{k.label}</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#9b2d7f' }}>{k.value}</div>
                </div>
              ))}
            </div>

            {/* Charts */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 18,
            }}>
              {/* Quotes per Month */}
              <div style={cardStyle}>
                <div style={chartTitleStyle}>Quotes per Month</div>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 12, marginTop: -6 }}>Number of quotes created each month</div>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={monthlyData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0e8ee" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} width={28} />
                    <Tooltip
                      formatter={(v) => [v, 'Quotes']}
                      contentStyle={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}
                    />
                    <Bar dataKey="count" fill="#9b2d7f" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Value per Month */}
              <div style={cardStyle}>
                <div style={chartTitleStyle}>Value per Month</div>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 12, marginTop: -6 }}>Total quoted value each month</div>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={monthlyData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0e8ee" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#888' }}
                      axisLine={false} tickLine={false} width={60}
                      tickFormatter={v => v >= 100000 ? `₹${(v/100000).toFixed(1)}L` : v > 0 ? `₹${Math.round(v).toLocaleString('en-IN')}` : '₹0'}
                    />
                    <Tooltip
                      formatter={(v) => [fmt(v), 'Value']}
                      contentStyle={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}
                    />
                    <Bar dataKey="value" fill="#c47aaa" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Best Month banner */}
            {bestMonth && (
              <div style={{
                marginTop: 18,
                background: '#fdf0f9',
                border: '1px solid #e8c4e0',
                borderRadius: 10,
                padding: '16px 24px',
                fontSize: 14,
                color: '#6b1f5a',
                fontWeight: 500,
              }}>
                Best Month: <strong>{bestMonth.label}</strong> — {fmt(bestMonth.value)} across {bestMonth.count} quote{bestMonth.count !== 1 ? 's' : ''}
              </div>
            )}

            {/* ── Quote Insights ──────────────────────────────────── */}
            <div style={{ marginTop: 36 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#333', marginBottom: 18 }}>Quote Insights</div>

              {/* Insights KPI row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 18,
                marginBottom: 18,
              }}>
                {/* Repeat Customers */}
                <div style={kpiCardStyle}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>Repeat Customers</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#9b2d7f' }}>{insights.repeatCustomers}</div>
                  <div style={interpStyle}>{insights.repeatCustomers} customers have come back</div>
                </div>

                {/* Quotes with Revisions */}
                <div style={kpiCardStyle}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>Quotes with Revisions</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#9b2d7f' }}>{insights.revisionRate}%</div>
                  <div style={interpStyle}>{insights.revisionRate}% of quotes were edited</div>
                </div>

                {/* Avg Edits per Quote */}
                <div style={kpiCardStyle}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>Avg Edits per Quote</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#9b2d7f' }}>{insights.avgRevisions}</div>
                  <div style={interpStyle}>edits on average per quote</div>
                </div>

                {/* Follow-up Needed */}
                <div style={kpiCardStyle}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>Follow-up Needed</div>
                  <div style={{ fontSize: 11, color: '#bbb', marginBottom: 8 }}>quoted but no update in 45 days</div>
                  <div style={{
                    fontSize: 28, fontWeight: 700,
                    color: insights.inactiveQuotes > 0 ? '#e05c5c' : '#9b2d7f',
                  }}>{insights.inactiveQuotes}</div>
                  <div style={{ ...interpStyle, color: insights.inactiveQuotes > 0 ? '#e05c5c' : '#888' }}>
                    quotes need a call
                  </div>
                </div>

                {/* Amount to Collect */}
                <div style={kpiCardStyle}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>Amount to Collect</div>
                  <div style={{ fontSize: 11, color: '#bbb', marginBottom: 8 }}>advances not yet collected</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#9b2d7f' }}>{fmt(insights.pipeline)}</div>
                  <div style={interpStyle}>pending from confirmed orders</div>
                </div>

                {/* Realisation Rate */}
                <div style={kpiCardStyle}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>Realisation Rate</div>
                  <div style={{ fontSize: 11, color: '#bbb', marginBottom: 8 }}>of expected advances collected</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#9b2d7f' }}>
                    {insights.realisationRate === null ? '—' : `${insights.realisationRate}%`}
                  </div>
                  <div style={interpStyle}>collected upfront on average</div>
                </div>
              </div>

              {/* Status Breakdown */}
              <div style={{ ...cardStyle, marginBottom: 18 }}>
                <div style={chartTitleStyle}>Status Breakdown</div>
                <div style={{ position: 'relative' }}>
                  {/* Segmented bar */}
                  <div style={{ display: 'flex', height: 32, borderRadius: 6, overflow: 'hidden', marginBottom: 16, background: '#f5f5f5' }}>
                    {insights.statusBreakdown.map(s => s.count > 0 && (
                      <div
                        key={s.status}
                        onMouseEnter={() => setHoveredStatus(s.status)}
                        onMouseLeave={() => setHoveredStatus(null)}
                        style={{
                          width: `${s.pct}%`,
                          background: STATUS_COLORS[s.status],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#fff',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          minWidth: 4,
                          cursor: 'default',
                          transition: 'opacity 0.15s',
                          opacity: hoveredStatus && hoveredStatus !== s.status ? 0.6 : 1,
                        }}
                      >
                        {s.pct >= 8 ? `${s.pct}%` : ''}
                      </div>
                    ))}
                  </div>

                  {/* Hover tooltip */}
                  {hoveredStatus && (() => {
                    const seg = insights.statusBreakdown.find(s => s.status === hoveredStatus)
                    if (!seg) return null
                    return (
                      <div style={{
                        position: 'absolute',
                        top: 36,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#1a1a1a',
                        color: '#fff',
                        borderRadius: 8,
                        padding: '10px 14px',
                        fontSize: 12,
                        zIndex: 20,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                        pointerEvents: 'none',
                        maxWidth: 360,
                        lineHeight: 1.6,
                      }}>
                        <div style={{ fontWeight: 700, marginBottom: 5, color: STATUS_COLORS[hoveredStatus] }}>
                          {hoveredStatus} — {seg.count} quote{seg.count !== 1 ? 's' : ''} ({seg.pct}%)
                        </div>
                        <div style={{ color: '#ccc', wordBreak: 'break-word' }}>
                          {seg.quoteNos.length > 0
                            ? (seg.quoteNos.slice(0, 12).join('  ·  ') + (seg.quoteNos.length > 12 ? `  +${seg.quoteNos.length - 12} more` : ''))
                            : 'No quotes'
                          }
                        </div>
                      </div>
                    )
                  })()}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
                  {insights.statusBreakdown.map(s => (
                    <div key={s.status} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLORS[s.status], flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: '#555' }}>{s.status}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#333' }}>{s.count}</span>
                      <span style={{ fontSize: 11, color: '#aaa' }}>({s.pct}%)</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top customers */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>
                {/* By Value */}
                <div style={cardStyle}>
                  <div style={chartTitleStyle}>Top Customers by Value</div>
                  {insights.top5ByValue.length === 0
                    ? <div style={{ color: '#bbb', fontSize: 13 }}>No data</div>
                    : insights.top5ByValue.map((row, i) => (
                      <div
                        key={row.name}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          padding: '9px 0',
                          borderBottom: i < insights.top5ByValue.length - 1 ? '1px solid #f5eff3' : 'none',
                          gap: 10,
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#c47aaa', width: 20, flexShrink: 0, paddingTop: 1 }}>{row.rank}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</div>
                          {row.lastDate && (
                            <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                              Last quoted {daysAgo(row.lastDate)} days ago
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#9b2d7f', flexShrink: 0, paddingTop: 1 }}>{fmt(row.value)}</span>
                      </div>
                    ))
                  }
                </div>

                {/* By Count */}
                <div style={cardStyle}>
                  <div style={chartTitleStyle}>Top Customers by Quote Count</div>
                  {insights.top5ByCount.length === 0
                    ? <div style={{ color: '#bbb', fontSize: 13 }}>No data</div>
                    : insights.top5ByCount.map((row, i) => (
                      <div
                        key={row.name}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          padding: '9px 0',
                          borderBottom: i < insights.top5ByCount.length - 1 ? '1px solid #f5eff3' : 'none',
                          gap: 10,
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#c47aaa', width: 20, flexShrink: 0, paddingTop: 1 }}>{row.rank}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</div>
                          {row.lastDate && (
                            <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                              Last quoted {daysAgo(row.lastDate)} days ago
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#9b2d7f', flexShrink: 0, paddingTop: 1 }}>{row.count} quote{row.count !== 1 ? 's' : ''}</span>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>

            {/* ── Product Insights ─────────────────────────────── */}
            <div style={{ marginTop: 36 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#333', marginBottom: 18 }}>Product Insights</div>

              {/* Curtain Segment Breakdown */}
              <div style={{ ...cardStyle, marginBottom: 18 }}>
                <div style={chartTitleStyle}>Most Used Curtain Segment</div>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 16, marginTop: -6 }}>Panel type distribution across all quotes</div>
                {productInsights.segments.map(seg => {
                  const isTop = seg.count > 0 && seg.count === productInsights.maxSegCount
                  return (
                    <div key={seg.type} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                        <span style={{ fontSize: 13, fontWeight: isTop ? 700 : 500, color: isTop ? '#9b2d7f' : '#555' }}>
                          {seg.type}
                        </span>
                        <span style={{ fontSize: 12, color: '#888' }}>
                          {seg.count} panel{seg.count !== 1 ? 's' : ''} ({seg.pct}%)
                        </span>
                      </div>
                      <div style={{ height: 10, borderRadius: 5, background: '#f5f5f5', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${seg.pct}%`,
                          borderRadius: 5,
                          background: isTop ? '#9b2d7f' : '#c47aaa',
                          transition: 'width 0.3s',
                        }} />
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Room types + Manufacturers side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18, marginBottom: 18 }}>
                <div style={cardStyle}>
                  <div style={chartTitleStyle}>Most Used Room Types</div>
                  {productInsights.top5RoomTypes.length === 0
                    ? <div style={{ color: '#bbb', fontSize: 13 }}>No data</div>
                    : productInsights.top5RoomTypes.map((row, i) => (
                      <div key={row.type} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: i < productInsights.top5RoomTypes.length - 1 ? '1px solid #f5eff3' : 'none', gap: 10 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#c47aaa', width: 20, flexShrink: 0 }}>{row.rank}</span>
                        <span style={{ flex: 1, fontSize: 13, color: '#333' }}>{row.type}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#9b2d7f', flexShrink: 0 }}>{row.count}</span>
                      </div>
                    ))
                  }
                </div>

                <div style={cardStyle}>
                  <div style={chartTitleStyle}>Most Used Manufacturers</div>
                  {productInsights.top5Mfrs.length === 0
                    ? <div style={{ color: '#bbb', fontSize: 13 }}>No data</div>
                    : productInsights.top5Mfrs.map((row, i) => (
                      <div key={row.mfr} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: i < productInsights.top5Mfrs.length - 1 ? '1px solid #f5eff3' : 'none', gap: 10 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#c47aaa', width: 20, flexShrink: 0 }}>{row.rank}</span>
                        <span style={{ flex: 1, fontSize: 13, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.mfr}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#9b2d7f', flexShrink: 0 }}>{row.count} panel{row.count !== 1 ? 's' : ''}</span>
                      </div>
                    ))
                  }
                </div>
              </div>

              {/* Product KPI row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 18, marginBottom: 18 }}>
                <div style={kpiCardStyle}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>Avg Discount Given</div>
                  <div style={{ fontSize: 11, color: '#bbb', marginBottom: 8 }}>across all fabric panels</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#9b2d7f' }}>
                    {productInsights.avgDisc === null ? '—' : `${productInsights.avgDisc}%`}
                  </div>
                  <div style={interpStyle}>average on panels with a discount</div>
                </div>

                <div style={kpiCardStyle}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>Avg Panels / Quote</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#9b2d7f' }}>
                    {productInsights.avgPanelsPerQuote ?? '—'}
                  </div>
                  <div style={interpStyle}>fabric panels per quote on average</div>
                </div>

                <div style={kpiCardStyle}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>Avg Rooms / Quote</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#9b2d7f' }}>
                    {productInsights.avgRoomsPerQuote ?? '—'}
                  </div>
                  <div style={interpStyle}>rooms covered per quote on average</div>
                </div>

                <div style={kpiCardStyle}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>Hardware Attach Rate</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#9b2d7f' }}>
                    {productInsights.hardwareAttachRate === null ? '—' : `${productInsights.hardwareAttachRate}%`}
                  </div>
                  <div style={interpStyle}>of quotes include hardware</div>
                </div>
              </div>

              {/* Fitting rate distribution */}
              <div style={cardStyle}>
                <div style={chartTitleStyle}>Fitting Rate Distribution</div>
                {productInsights.fittingDist.length === 0
                  ? <div style={{ color: '#bbb', fontSize: 13 }}>No data</div>
                  : productInsights.fittingDist.map((row, i) => (
                    <div key={row.rate} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: i < productInsights.fittingDist.length - 1 ? '1px solid #f5eff3' : 'none', gap: 10 }}>
                      <span style={{ flex: 1, fontSize: 13, color: '#333' }}>₹{row.rate} / window</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#9b2d7f', flexShrink: 0 }}>{row.count} quote{row.count !== 1 ? 's' : ''}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
